import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Spinner } from '../components/loading';
import { pmLaunchAPI, type PmLaunchPageKey } from '../services/api';
import { launchIdFromText, manageBudgetsLaunchAPI } from '../services/hiringRecruitmentApi';

/**
 * The tab that opens a page of 1Office Project Management for the signed-in employee.
 *
 *   /open/project-management/<page>?project=<ChannelPlex project id>
 *   /open/project-management/manage-budgets?project=<id>&budget=<budget id>&opening=<opening id>
 *
 * Manage Budgets may be opened ON a budget and one of its openings (the 1Office team's reply
 * of 15 October 2026, B4): this page passes the two numbers on to the API, which asks 1Office
 * for a link that opens that budget's Openings window with the opening highlighted. Each must
 * be plain digits from 1 to 2147483647, or it is left out (an opening without its budget too),
 * and the page opens as it always did. They are never a grant: who gets a link is the API's
 * answer, exactly as without them. Any other page ignores them.
 *
 * WHY A PAGE OF ITS OWN, opened in a new tab by an ordinary link: the API has to ask
 * 1Office for a one-time sign-in link, and that can take from a second to most of a
 * minute (1Office's sign-in service starts cold). A `window.open` made after a wait that
 * long is blocked by the browser as a pop-up. So the click opens THIS page at once — it
 * is already signed in to ChannelPlex, because the sign-in is kept in localStorage — and
 * this page asks for the link and then replaces itself with it.
 *
 * The link is a one-time sign-in to 1Office, so it is handled like a password:
 *   - asked for only here, at the moment of opening, and never kept in state or storage;
 *   - opened with location.replace, so this page does not stay in the tab's history and
 *     "Back" cannot ask for a second link by accident;
 *   - opened exactly as received — the one-time code is in the part after the `#`;
 *   - never written to the console.
 * Only an https link is ever opened; the API has already checked where it points.
 *
 * This route sits OUTSIDE the dashboard layout on purpose: no menu call, no project
 * switcher — a tab that exists for a few seconds needs neither.
 */

/** What each page the API can open is called. Mirrors PM_LAUNCH_PAGES on the API. */
const PAGE_TITLES: Readonly<Record<PmLaunchPageKey, string>> = {
  'attrition-classification': 'Managed vs Unmanaged Attrition',
  'manage-budgets': 'Manage Budgets',
};

const isPageKey = (raw: string | undefined): raw is PmLaunchPageKey =>
  typeof raw === 'string' && Object.prototype.hasOwnProperty.call(PAGE_TITLES, raw);

/** A ChannelPlex project id from the query string: plain digits only. */
const parseProjectId = (raw: string | null): number | null => {
  if (!raw || !/^[1-9]\d{0,15}$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
};

/** Leaving ChannelPlex for the link. A seam: a test replaces `go`, a browser never does. */
export const pmLaunchNavigator = {
  go: (url: string): void => {
    window.location.replace(url);
  },
};

/** No answer, or a refusal with no sentence of its own: it names the page that was being opened, as the heading above it does. */
const fallbackFor = (title: string): string => `Could not open ${title}. Please try again in a moment.`;
// A 404 from the link route means the feature has been stopped on the server on purpose
// (it is on by default). Trying again cannot help until it is brought back.
const TURNED_OFF = 'This link has been turned off for now. Please go back to ChannelPlex and try again later.';
const SIGNED_OUT = 'You are signed out of ChannelPlex. Sign in again, then open the link once more.';
const TOO_MANY = 'Too many links were asked for in the last minute. Please wait a minute and try again.';
const BAD_ANSWER = 'ChannelPlex did not receive a link it can open. Please tell the ChannelPlex team.';

/** The sentence to show for a refused request — the API's own message whenever it sent one. `title` is the page being opened. */
const messageFor = (err: unknown, title: string): string => {
  const response = (err as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  const sent = response?.data?.message;
  if (response?.status === 404) return TURNED_OFF;
  if (response?.status === 401) return SIGNED_OUT;
  if (typeof sent === 'string' && sent.trim()) return sent;
  if (response?.status === 429) return TOO_MANY;
  return fallbackFor(title);
};

type State = { phase: 'opening' } | { phase: 'failed'; message: string; canRetry: boolean };

export default function OpenProjectManagementPage() {
  const { page } = useParams<{ page: string }>();
  const [searchParams] = useSearchParams();
  const projectId = parseProjectId(searchParams.get('project'));
  const pageKey = isPageKey(page) ? page : null;
  // Manage Budgets only: the budget to open the page on, and the opening to highlight in it.
  const budgetId = pageKey === 'manage-budgets' ? launchIdFromText(searchParams.get('budget')) : null;
  const openingId = budgetId != null ? launchIdFromText(searchParams.get('opening')) : null;
  const title = pageKey ? PAGE_TITLES[pageKey] : 'Project Management';

  const [state, setState] = useState<State>({ phase: 'opening' });
  // One request per open. Effects can run twice (a dev build, a remount); a second
  // request would only spend a second one-time link.
  const started = useRef(false);
  const busy = useRef(false);
  const retryButton = useRef<HTMLButtonElement>(null);

  // Pressing "Try again" replaces the failure view (and the focused button) with
  // "Opening…". If the retry fails too, put focus back on the new button, so a keyboard
  // user can press it again without hunting for it.
  useEffect(() => {
    if (state.phase === 'failed' && state.canRetry) retryButton.current?.focus();
  }, [state]);

  const open = useCallback(async () => {
    if (busy.current) return;
    if (!pageKey || projectId == null) {
      setState({ phase: 'failed', message: 'This link is not complete. Please open it again from ChannelPlex.', canRetry: false });
      return;
    }
    busy.current = true;
    setState({ phase: 'opening' });
    try {
      const res = budgetId != null
        ? await manageBudgetsLaunchAPI.requestLink({ projectId, budgetId, openingId })
        : await pmLaunchAPI.requestLink({ projectId, page: pageKey });
      const launchUrl: unknown = res?.data?.data?.launchUrl;
      if (typeof launchUrl !== 'string' || !launchUrl.startsWith('https://')) {
        setState({ phase: 'failed', message: BAD_ANSWER, canRetry: true });
        return;
      }
      // Stays on "Opening…" while the browser leaves. Not put in state, not logged.
      pmLaunchNavigator.go(launchUrl);
    } catch (err) {
      const turnedOff = (err as { response?: { status?: number } })?.response?.status === 404;
      setState({ phase: 'failed', message: messageFor(err, PAGE_TITLES[pageKey]), canRetry: !turnedOff });
    } finally {
      busy.current = false;
    }
  }, [pageKey, projectId, budgetId, openingId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void open();
  }, [open]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
        {state.phase === 'opening' ? (
          <div role="status" aria-live="polite">
            <div className="flex justify-center">
              <Spinner size="lg" />
            </div>
            <h1 className="mt-4 text-base font-semibold text-gray-900">Opening {title}…</h1>
            <p className="mt-2 text-sm text-gray-600">
              Getting a one-time sign-in from 1Office Project Management. The first time in a while, this can take up to a minute.
            </p>
            <p className="mt-3 text-xs text-gray-500">
              The page opens with your own Project Management access and shows your Project Management projects. If this browser is
              already signed in to 1Office, it opens under that sign-in. If you land on the 1Office home or sign-in page instead,
              1Office did not open the page for your account — most often because it has no Project Management access.
            </p>
          </div>
        ) : (
          <div role="alert">
            <AlertCircle className="mx-auto h-8 w-8 text-red-500" aria-hidden="true" />
            <h1 className="mt-4 text-base font-semibold text-gray-900">Could not open {title}</h1>
            <p className="mt-2 text-sm text-gray-700">{state.message}</p>
            {state.canRetry && (
              <button
                ref={retryButton}
                type="button"
                onClick={() => void open()}
                className="mt-4 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Try again
              </button>
            )}
            <p className="mt-3 text-xs text-gray-500">You can close this tab and go back to ChannelPlex.</p>
          </div>
        )}
      </div>
    </div>
  );
}
